import { useCallback, useEffect, useState } from 'react'
import {
  VISUAL_DEBUG_CHANGE_EVENT,
  VISUAL_DEBUG_POLL_MS,
  installVisualDebugConsoleToggle,
  readVisualDebugEnabled,
  setVisualDebugEnabled,
} from '@/lib/debug/visual-debug-flag'

export type UseVisualDebugResult = {
  enabled: boolean
  setEnabled: (next: boolean) => void
  toggle: () => void
}

/**
 * Subscribe to the visual-layer debug flag.
 *
 * Stays in sync however the flag is set — the in-app toggle, `__larryVisualDebug()`, or a raw
 * localStorage edit. The last of those is why this polls in addition to listening: the DOM `storage`
 * event fires only in *other* tabs, so a console edit in this one is otherwise invisible until a
 * reload. One localStorage read per second is free.
 */
export function useVisualDebug(): UseVisualDebugResult {
  const [enabled, setEnabledState] = useState(readVisualDebugEnabled)

  useEffect(() => {
    installVisualDebugConsoleToggle()

    const sync = () => setEnabledState(readVisualDebugEnabled())

    sync()

    const interval = window.setInterval(sync, VISUAL_DEBUG_POLL_MS)

    window.addEventListener(VISUAL_DEBUG_CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener(VISUAL_DEBUG_CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    setVisualDebugEnabled(next)
    // Optimistic: the event listener above would catch this anyway, but updating here keeps the
    // control responsive if the write itself was rejected by the browser.
    setEnabledState(next)
  }, [])

  const toggle = useCallback(() => {
    setEnabled(!readVisualDebugEnabled())
  }, [setEnabled])

  return { enabled, setEnabled, toggle }
}
